import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StatusComponent } from '../../ui/status/status.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { CampaignOutcomeComponent } from './campaign-outcome.component';
import { CampaignNextSendComponent } from './campaign-next-send.component';
import { formatWhen } from '../../../../utils/sms';
import {
  canStillSend,
  formatRun,
  formatRunLength,
  hasNotSent,
  statusGroupOf,
  CampaignStatusGroup,
} from '../../../pages/organization/campaigns/campaign-list/campaign-facts';

/**
 * A word app-status reads as each group's dot colour, so rows share the
 * colour of the header counts and the filter.
 */
const TONE_WORD: Record<CampaignStatusGroup['dot'], string> = {
  ok: 'completed',
  wait: 'scheduled',
  busy: 'active',
  bad: 'failed',
  idle: 'draft',
};

/** One list row, worked out once per page so the template only reads fields. */
interface CampaignRow {
  campaign: any;
  key: string | number;
  name: string;
  description: string;
  /** The status group's name ("Running"), else the raw backend status, or '' when the API sent none. */
  status: string;
  /** What app-status colours the dot by. */
  tone: string;
  run: string;
  runLength: string | null;
  created: string;
  /** Can still send, so the next-send cell has something to say. */
  live: boolean;
  /** Nothing has gone out, so there is no delivery to show. */
  unsent: boolean;
}

/**
 * The campaign log: name over description, status, delivery outcome, next
 * send, run dates and created. Rows open the campaign; report, duplicate
 * and delete sit inline at the end of the row.
 */
@Component({
  selector: 'app-campaign-table',
  standalone: true,
  imports: [
    MatTooltipModule,
    StatusComponent,
    HasPermissionDirective,
    DataTableComponent,
    CampaignOutcomeComponent,
    CampaignNextSendComponent,
  ],
  templateUrl: './campaign-table.component.html',
  styleUrl: './campaign-table.component.css',
})
export class CampaignTableComponent implements OnChanges, OnInit {
  private router = inject(Router);
  private permissions = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @Input({ required: true }) tableItems: any = [];
  @Input() loading: boolean = false;
  /** Id of the campaign being copied into the wizard, to show it is working. */
  @Input() duplicatingId: number | string | null = null;
  @Output() showDelete = new EventEmitter();
  /** Emits the campaign row to copy into a new, unsaved campaign. */
  @Output() duplicate = new EventEmitter<any>();

  rows: CampaignRow[] = [];
  /** "View details" needed this permission; opening a row still does. */
  canView = false;
  /** Countdowns read this; it moves once a minute. */
  now = new Date();

  ngOnChanges() {
    const items: any[] = Array.isArray(this.tableItems) ? this.tableItems : [];
    this.rows = items
      .filter((item) => !!item && typeof item === 'object')
      .map((campaign, index) => {
        const raw = String(campaign.status ?? '').trim();
        const group = statusGroupOf(raw);
        return {
          campaign,
          key: campaign.id ?? campaign.uuid ?? `row-${index}`,
          name: String(campaign.name ?? '').trim() || 'Untitled campaign',
          description: String(campaign.description ?? '').trim(),
          // Same word and colour as the header counts and the status filter.
          status: group?.title ?? raw,
          tone: group ? TONE_WORD[group.dot] : raw,
          run: formatRun(campaign.startDate, campaign.endDate),
          runLength: formatRunLength(campaign.startDate, campaign.endDate),
          created: formatWhen(campaign.createdAt),
          live: canStillSend(campaign.status),
          unsent: hasNotSent(campaign.status),
        };
      });
  }

  ngOnInit() {
    this.permissions
      .hasPermission('CAMPAIGN_MANAGEMENT', 'view-campaigns')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allowed) => (this.canView = allowed));

    if (this.isBrowser) {
      // Re-read the clock each minute so "in 4 min" counts down. The timer
      // runs outside Angular so it never holds the app "unstable".
      const timer = this.zone.runOutsideAngular(() =>
        setInterval(() => this.zone.run(() => (this.now = new Date())), 60_000)
      );
      this.destroyRef.onDestroy(() => clearInterval(timer));
    }
  }

  navigateToDetails(id: string | number | null, uuid: string) {
    if (id) {
      this.router.navigate(['/organization/campaigns', id, uuid]);
    }
  }

  openRow(row: CampaignRow) {
    if (!this.canView) return;
    this.navigateToDetails(row.campaign.id, row.campaign.uuid);
  }

  /** Enter on the row opens it; Enter on a row button stays with the button. */
  openFromKeyboard(event: Event, row: CampaignRow) {
    if (event.target !== event.currentTarget) return;
    this.openRow(row);
  }

  openReport(row: CampaignRow) {
    if (row.campaign.id) {
      this.router.navigate(['/organization/campaigns', row.campaign.id, row.campaign.uuid, 'reports']);
    }
  }

  isDuplicating(row: CampaignRow): boolean {
    return this.duplicatingId !== null && this.duplicatingId === row.campaign.id;
  }
}
